'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DistanceCalculator() {
  const t = useTranslations("tools.distanceCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState('2d');
  const [activeSubTab, setActiveSubTab] = useState('decimal');

  // 2D Calculator State
  const [inputs2D, setInputs2D] = useState({
    x1: 1,
    y1: 1,
    x2: 4,
    y2: 5
  });
  const [results2D, setResults2D] = useState<any>(null);

  // 3D Calculator State
  const [inputs3D, setInputs3D] = useState({
    x1: 1,
    y1: 1,
    z1: 1,
    x2: 2,
    y2: 2,
    z2: 2
  });
  const [results3D, setResults3D] = useState<any>(null);

  // Lat/Long Decimal State
  const [inputsLatLongDec, setInputsLatLongDec] = useState({
    lat1: 38.8976,
    lon1: -77.0366,
    lat2: 39.9496,
    lon2: -75.1503
  });

  // Lat/Long DMS State
  const [inputsLatLongDMS, setInputsLatLongDMS] = useState({
    lat1Deg: 38,
    lat1Min: 53,
    lat1Sec: 51.36,
    lat1Dir: 'N',
    lon1Deg: 77,
    lon1Min: 2,
    lon1Sec: 11.76,
    lon1Dir: 'W',
    lat2Deg: 39,
    lat2Min: 56,
    lat2Sec: 58.56,
    lat2Dir: 'N',
    lon2Deg: 75,
    lon2Min: 9,
    lon2Sec: 1.08,
    lon2Dir: 'W'
  });

  const [resultsLatLong, setResultsLatLong] = useState<any>(null);

  // Map State
  const [mapMarkers, setMapMarkers] = useState<any[]>([]);
  const [resultsMap, setResultsMap] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '3rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '800px' },
      { maxWidth: '1000px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    tabsContainer: getResponsiveStyle(
      { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const, justifyContent: 'center' as const },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2rem' }
    ),
    tabBtn: getResponsiveStyle(
      {
        padding: '0.75rem 1.5rem',
        border: '2px solid #cbd5e1',
        background: 'white',
        color: '#64748b',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'all 0.3s ease',
        fontSize: '0.95rem'
      },
      { padding: '0.625rem 1rem', fontSize: '0.875rem' },
      {},
      {}
    ),
    tabBtnActive: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderColor: '#2563eb'
    },
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(2, 1fr)' }
    ),
    pointSection: getResponsiveStyle(
      { background: '#f8fafc', borderRadius: '0.75rem', marginBottom: '1.5rem' },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),
    pointTitle: {
      color: '#475569',
      marginBottom: '1rem',
      fontSize: '1.1rem',
      fontWeight: 600
    },
    formGroup: {
      marginBottom: '1rem'
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#334155',
      fontWeight: 600,
      fontSize: '0.95rem'
    },
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem' },
      { padding: '0.875rem 1rem' },
      { padding: '1rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        background: 'white'
      },
      { padding: '0.75rem' },
      { padding: '0.875rem 1rem' },
      { padding: '1rem' }
    ),
    coordinateGroup: getResponsiveStyle(
      { display: 'grid', gap: '1rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    dmsGroup: getResponsiveStyle(
      { display: 'grid', gap: '0.5rem', alignItems: 'end' as const },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr 1fr 80px' },
      { gridTemplateColumns: '1fr 1fr 1fr 80px' }
    ),
    calculateBtn: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        width: '100%',
        maxWidth: '300px',
        display: 'block',
        margin: '0 auto'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' },
      { padding: '1rem 2.5rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '1rem',
        marginTop: '1.5rem',
        border: '2px solid #86efac'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: {
      color: '#166534',
      marginBottom: '1.5rem',
      fontSize: '1.5rem',
      textAlign: 'center' as const,
      fontWeight: 600
    },
    resultItem: getResponsiveStyle(
      {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'white',
        borderRadius: '0.5rem',
        marginBottom: '0.75rem'
      },
      { padding: '0.875rem', flexDirection: 'column' as const, gap: '0.5rem' },
      { padding: '1rem', flexDirection: 'row' as const },
      { padding: '1rem', flexDirection: 'row' as const }
    ),
    resultLabel: {
      color: '#475569',
      fontWeight: 600
    },
    resultValue: getResponsiveStyle(
      { color: '#2563eb', fontWeight: 700 },
      { fontSize: '1.1rem' },
      { fontSize: '1.2rem' },
      { fontSize: '1.2rem' }
    ),
    formulaDisplay: getResponsiveStyle(
      {
        background: '#f1f5f9',
        borderRadius: '0.5rem',
        marginTop: '1rem',
        fontFamily: "'Courier New', monospace",
        color: '#475569',
        overflowX: 'auto' as const
      },
      { padding: '0.875rem', fontSize: '0.8rem' },
      { padding: '1rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '0.9rem' }
    ),
    mapContainer: getResponsiveStyle(
      {
        width: '100%',
        borderRadius: '0.75rem',
        overflow: 'hidden',
        marginBottom: '1rem',
        border: '2px solid #e2e8f0',
        position: 'relative' as const
      },
      { height: '300px' },
      { height: '400px' },
      { height: '400px' }
    ),
    mapInstructions: getResponsiveStyle(
      {
        background: '#fef3c7',
        borderRadius: '0.5rem',
        marginBottom: '1rem',
        borderLeft: '4px solid #f59e0b'
      },
      { padding: '0.875rem', fontSize: '0.875rem' },
      { padding: '1rem', fontSize: '0.95rem' },
      { padding: '1rem', fontSize: '0.95rem' }
    ),
    clearBtn: getResponsiveStyle(
      {
        background: '#ef4444',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      },
      { padding: '0.625rem 1.25rem', fontSize: '0.875rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem', marginBottom: '2rem' },
      { padding: '2.5rem', marginBottom: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary
    }
  };

  // Calculation Functions
  const handleCalculate2D = (e: React.FormEvent) => {
    e.preventDefault();
    const { x1, y1, x2, y2 } = inputs2D;
    const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    setResults2D({
      distance: distance.toFixed(4),
      formula: `d = √[(${x2} - ${x1})² + (${y2} - ${y1})²] = √[${(x2-x1).toFixed(2)}² + ${(y2-y1).toFixed(2)}²] = ${distance.toFixed(4)}`
    });
  };

  const handleCalculate3D = (e: React.FormEvent) => {
    e.preventDefault();
    const { x1, y1, z1, x2, y2, z2 } = inputs3D;
    const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2) + Math.pow(z2 - z1, 2));
    setResults3D({
      distance: distance.toFixed(4),
      formula: `d = √[(${x2} - ${x1})² + (${y2} - ${y1})² + (${z2} - ${z1})²] = ${distance.toFixed(4)}`
    });
  };

  const dmsToDecimal = (deg: number, min: number, sec: number, dir: string) => {
    let decimal = Math.abs(deg) + min / 60 + sec / 3600;
    if (dir === 'S' || dir === 'W') {
      decimal = -decimal;
    }
    return decimal;
  };

  const calculateGeoDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const f = 1 / 298.257;
    const toRad = Math.PI / 180;

    let lat1Lambert = Math.atan((1 - f) * Math.tan(lat1 * toRad));
    let lat2Lambert = Math.atan((1 - f) * Math.tan(lat2 * toRad));

    const deltaLat = lat2Lambert - lat1Lambert;
    const deltaLon = (lon2 - lon1) * toRad;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
             Math.cos(lat1Lambert) * Math.cos(lat2Lambert) *
             Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const P = (lat1Lambert + lat2Lambert) / 2;
    const Q = (lat2Lambert - lat1Lambert) / 2;
    const X = (c - Math.sin(c)) * Math.sin(P) * Math.sin(P) * Math.cos(Q) * Math.cos(Q) /
             Math.cos(c / 2) / Math.cos(c / 2);
    const Y = (c + Math.sin(c)) * Math.sin(Q) * Math.sin(Q) * Math.cos(P) * Math.cos(P) /
             Math.sin(c / 2) / Math.sin(c / 2);

    const distanceKm = 6378.1 * (c - f * (X + Y) / 2);
    const distanceMiles = distanceKm / 1.60935;

    return { km: distanceKm, miles: distanceMiles };
  };

  const handleCalculateLatLongDecimal = (e: React.FormEvent) => {
    e.preventDefault();
    const { lat1, lon1, lat2, lon2 } = inputsLatLongDec;
    const result = calculateGeoDistance(lat1, lon1, lat2, lon2);
    setResultsLatLong(result);
  };

  const handleCalculateLatLongDMS = (e: React.FormEvent) => {
    e.preventDefault();
    const lat1 = dmsToDecimal(
      inputsLatLongDMS.lat1Deg,
      inputsLatLongDMS.lat1Min,
      inputsLatLongDMS.lat1Sec,
      inputsLatLongDMS.lat1Dir
    );
    const lon1 = dmsToDecimal(
      inputsLatLongDMS.lon1Deg,
      inputsLatLongDMS.lon1Min,
      inputsLatLongDMS.lon1Sec,
      inputsLatLongDMS.lon1Dir
    );
    const lat2 = dmsToDecimal(
      inputsLatLongDMS.lat2Deg,
      inputsLatLongDMS.lat2Min,
      inputsLatLongDMS.lat2Sec,
      inputsLatLongDMS.lat2Dir
    );
    const lon2 = dmsToDecimal(
      inputsLatLongDMS.lon2Deg,
      inputsLatLongDMS.lon2Min,
      inputsLatLongDMS.lon2Sec,
      inputsLatLongDMS.lon2Dir
    );
    const result = calculateGeoDistance(lat1, lon1, lat2, lon2);
    setResultsLatLong(result);
  };

  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mapMarkers.length >= 2) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 360 - 180;
    const y = 90 - ((e.clientY - rect.top) / rect.height) * 180;

    const newMarker = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      lat: y.toFixed(4),
      lon: x.toFixed(4)
    };

    const newMarkers = [...mapMarkers, newMarker];
    setMapMarkers(newMarkers);

    if (newMarkers.length === 2) {
      const lat1 = parseFloat(newMarkers[0].lat);
      const lon1 = parseFloat(newMarkers[0].lon);
      const lat2 = parseFloat(newMarkers[1].lat);
      const lon2 = parseFloat(newMarkers[1].lon);

      const result = calculateGeoDistance(lat1, lon1, lat2, lon2);
      setResultsMap({
        point1: `[${lat1.toFixed(4)}, ${lon1.toFixed(4)}]`,
        point2: `[${lat2.toFixed(4)}, ${lon2.toFixed(4)}]`,
        ...result
      });
    }
  };

  const handleClearMap = () => {
    setMapMarkers([]);
    setResultsMap(null);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t("toolTitle")}</h2>
          </div>

          <div style={styles.tabsContainer}>
            <button
              style={{...styles.tabBtn, ...(activeTab === '2d' ? styles.tabBtnActive : {})}}
              onClick={() => setActiveTab('2d')}
            >
              {t("tab2D")}
            </button>
            <button
              style={{...styles.tabBtn, ...(activeTab === '3d' ? styles.tabBtnActive : {})}}
              onClick={() => setActiveTab('3d')}
            >
              {t("tab3D")}
            </button>
            <button
              style={{...styles.tabBtn, ...(activeTab === 'latlong' ? styles.tabBtnActive : {})}}
              onClick={() => setActiveTab('latlong')}
            >
              {t("tabLatLong")}
            </button>
            <button
              style={{...styles.tabBtn, ...(activeTab === 'map' ? styles.tabBtnActive : {})}}
              onClick={() => setActiveTab('map')}
            >
              {t("tabMap")}
            </button>
          </div>

          {/* 2D Distance Calculator */}
          {activeTab === '2d' && (
            <div style={styles.calculatorCard}>
              <form onSubmit={handleCalculate2D}>
                <div style={styles.formGrid}>
                  <div style={styles.pointSection}>
                    <h4 style={styles.pointTitle}>{t("point1")}</h4>
                    <div style={styles.coordinateGroup}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("x1Label")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputs2D.x1}
                          onChange={(e) => setInputs2D({...inputs2D, x1: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("y1Label")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputs2D.y1}
                          onChange={(e) => setInputs2D({...inputs2D, y1: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div style={styles.pointSection}>
                    <h4 style={styles.pointTitle}>{t("point2")}</h4>
                    <div style={styles.coordinateGroup}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("x2Label")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputs2D.x2}
                          onChange={(e) => setInputs2D({...inputs2D, x2: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("y2Label")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputs2D.y2}
                          onChange={(e) => setInputs2D({...inputs2D, y2: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <button type="submit" style={styles.calculateBtn}>
                  {t("calculateButton")}
                </button>
              </form>

              {results2D && (
                <div style={styles.results}>
                  <h3 style={styles.resultsTitle}>{t("resultTitle")}</h3>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("distanceLabel")}</span>
                    <span style={styles.resultValue}>{results2D.distance}</span>
                  </div>
                  <div style={styles.formulaDisplay}>{results2D.formula}</div>
                </div>
              )}
            </div>
          )}

          {/* 3D Distance Calculator */}
          {activeTab === '3d' && (
            <div style={styles.calculatorCard}>
              <form onSubmit={handleCalculate3D}>
                <div style={styles.formGrid}>
                  <div style={styles.pointSection}>
                    <h4 style={styles.pointTitle}>{t("point1")}</h4>
                    <div style={styles.coordinateGroup}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("x1Label")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputs3D.x1}
                          onChange={(e) => setInputs3D({...inputs3D, x1: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("y1Label")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputs3D.y1}
                          onChange={(e) => setInputs3D({...inputs3D, y1: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("z1Label")}</label>
                      <input
                        type="number"
                        step="any"
                        value={inputs3D.z1}
                        onChange={(e) => setInputs3D({...inputs3D, z1: parseFloat(e.target.value) || 0})}
                        style={styles.input}
                        required
                      />
                    </div>
                  </div>

                  <div style={styles.pointSection}>
                    <h4 style={styles.pointTitle}>{t("point2")}</h4>
                    <div style={styles.coordinateGroup}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("x2Label")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputs3D.x2}
                          onChange={(e) => setInputs3D({...inputs3D, x2: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("y2Label")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputs3D.y2}
                          onChange={(e) => setInputs3D({...inputs3D, y2: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("z2Label")}</label>
                      <input
                        type="number"
                        step="any"
                        value={inputs3D.z2}
                        onChange={(e) => setInputs3D({...inputs3D, z2: parseFloat(e.target.value) || 0})}
                        style={styles.input}
                        required
                      />
                    </div>
                  </div>
                </div>

                <button type="submit" style={styles.calculateBtn}>
                  {t("calculateButton")}
                </button>
              </form>

              {results3D && (
                <div style={styles.results}>
                  <h3 style={styles.resultsTitle}>{t("resultTitle")}</h3>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("distanceLabel")}</span>
                    <span style={styles.resultValue}>{results3D.distance}</span>
                  </div>
                  <div style={styles.formulaDisplay}>{results3D.formula}</div>
                </div>
              )}
            </div>
          )}

          {/* Lat/Long Calculator */}
          {activeTab === 'latlong' && (
            <div style={styles.calculatorCard}>
              <div style={{...styles.tabsContainer, marginBottom: '1.5rem'}}>
                <button
                  style={{...styles.tabBtn, ...(activeSubTab === 'decimal' ? styles.tabBtnActive : {})}}
                  onClick={() => setActiveSubTab('decimal')}
                >
                  {t("decimalTab")}
                </button>
                <button
                  style={{...styles.tabBtn, ...(activeSubTab === 'dms' ? styles.tabBtnActive : {})}}
                  onClick={() => setActiveSubTab('dms')}
                >
                  {t("dmsTab")}
                </button>
              </div>

              {activeSubTab === 'decimal' && (
                <form onSubmit={handleCalculateLatLongDecimal}>
                  <div style={styles.formGrid}>
                    <div style={styles.pointSection}>
                      <h4 style={styles.pointTitle}>{t("point1")}</h4>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("latitudeLabel")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputsLatLongDec.lat1}
                          onChange={(e) => setInputsLatLongDec({...inputsLatLongDec, lat1: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("longitudeLabel")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputsLatLongDec.lon1}
                          onChange={(e) => setInputsLatLongDec({...inputsLatLongDec, lon1: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                    </div>

                    <div style={styles.pointSection}>
                      <h4 style={styles.pointTitle}>{t("point2")}</h4>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("latitudeLabel")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputsLatLongDec.lat2}
                          onChange={(e) => setInputsLatLongDec({...inputsLatLongDec, lat2: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>{t("longitudeLabel")}</label>
                        <input
                          type="number"
                          step="any"
                          value={inputsLatLongDec.lon2}
                          onChange={(e) => setInputsLatLongDec({...inputsLatLongDec, lon2: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <button type="submit" style={styles.calculateBtn}>
                    {t("calculateButton")}
                  </button>
                </form>
              )}

              {activeSubTab === 'dms' && (
                <form onSubmit={handleCalculateLatLongDMS}>
                  <div style={styles.pointSection}>
                    <h4 style={styles.pointTitle}>{t("point1")}</h4>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("latitudeLabel")}</label>
                      <div style={styles.dmsGroup}>
                        <input
                          type="number"
                          placeholder={t("degPlaceholder")}
                          value={inputsLatLongDMS.lat1Deg}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lat1Deg: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <input
                          type="number"
                          placeholder={t("minPlaceholder")}
                          min="0"
                          max="59"
                          value={inputsLatLongDMS.lat1Min}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lat1Min: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <input
                          type="number"
                          placeholder={t("secPlaceholder")}
                          step="any"
                          min="0"
                          max="59.99"
                          value={inputsLatLongDMS.lat1Sec}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lat1Sec: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <select
                          value={inputsLatLongDMS.lat1Dir}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lat1Dir: e.target.value})}
                          style={styles.select}
                        >
                          <option value="N">N</option>
                          <option value="S">S</option>
                        </select>
                      </div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("longitudeLabel")}</label>
                      <div style={styles.dmsGroup}>
                        <input
                          type="number"
                          placeholder={t("degPlaceholder")}
                          value={inputsLatLongDMS.lon1Deg}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lon1Deg: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <input
                          type="number"
                          placeholder={t("minPlaceholder")}
                          min="0"
                          max="59"
                          value={inputsLatLongDMS.lon1Min}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lon1Min: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <input
                          type="number"
                          placeholder={t("secPlaceholder")}
                          step="any"
                          min="0"
                          max="59.99"
                          value={inputsLatLongDMS.lon1Sec}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lon1Sec: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <select
                          value={inputsLatLongDMS.lon1Dir}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lon1Dir: e.target.value})}
                          style={styles.select}
                        >
                          <option value="E">E</option>
                          <option value="W">W</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div style={styles.pointSection}>
                    <h4 style={styles.pointTitle}>{t("point2")}</h4>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("latitudeLabel")}</label>
                      <div style={styles.dmsGroup}>
                        <input
                          type="number"
                          placeholder={t("degPlaceholder")}
                          value={inputsLatLongDMS.lat2Deg}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lat2Deg: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <input
                          type="number"
                          placeholder={t("minPlaceholder")}
                          min="0"
                          max="59"
                          value={inputsLatLongDMS.lat2Min}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lat2Min: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <input
                          type="number"
                          placeholder={t("secPlaceholder")}
                          step="any"
                          min="0"
                          max="59.99"
                          value={inputsLatLongDMS.lat2Sec}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lat2Sec: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <select
                          value={inputsLatLongDMS.lat2Dir}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lat2Dir: e.target.value})}
                          style={styles.select}
                        >
                          <option value="N">N</option>
                          <option value="S">S</option>
                        </select>
                      </div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("longitudeLabel")}</label>
                      <div style={styles.dmsGroup}>
                        <input
                          type="number"
                          placeholder={t("degPlaceholder")}
                          value={inputsLatLongDMS.lon2Deg}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lon2Deg: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <input
                          type="number"
                          placeholder={t("minPlaceholder")}
                          min="0"
                          max="59"
                          value={inputsLatLongDMS.lon2Min}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lon2Min: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <input
                          type="number"
                          placeholder={t("secPlaceholder")}
                          step="any"
                          min="0"
                          max="59.99"
                          value={inputsLatLongDMS.lon2Sec}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lon2Sec: parseFloat(e.target.value) || 0})}
                          style={styles.input}
                          required
                        />
                        <select
                          value={inputsLatLongDMS.lon2Dir}
                          onChange={(e) => setInputsLatLongDMS({...inputsLatLongDMS, lon2Dir: e.target.value})}
                          style={styles.select}
                        >
                          <option value="E">E</option>
                          <option value="W">W</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <button type="submit" style={styles.calculateBtn}>
                    {t("calculateButton")}
                  </button>
                </form>
              )}

              {resultsLatLong && (
                <div style={styles.results}>
                  <h3 style={styles.resultsTitle}>{t("resultTitle")}</h3>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("distanceLabel")}</span>
                    <span style={styles.resultValue}>{resultsLatLong.km.toFixed(2)} km</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("inMilesLabel")}</span>
                    <span style={styles.resultValue}>{resultsLatLong.miles.toFixed(2)} {t("milesUnit")}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Map Distance Calculator */}
          {activeTab === 'map' && (
            <div style={styles.calculatorCard}>
              <div style={styles.mapInstructions}>
                <p style={{margin: 0, color: '#92400e'}}>{t("mapInstructions")}</p>
              </div>

              <div style={styles.mapContainer} onClick={handleMapClick}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: '#e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b',
                  fontSize: '1.1rem',
                  position: 'relative'
                }}>
                  {mapMarkers.length === 0 && t("mapPlaceholder")}
                  {mapMarkers.map((marker, index) => (
                    <div
                      key={index}
                      style={{
                        position: 'absolute',
                        width: '20px',
                        height: '20px',
                        background: '#ef4444',
                        border: '3px solid white',
                        borderRadius: '50%',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        left: `${marker.x - 10}px`,
                        top: `${marker.y - 10}px`,
                        pointerEvents: 'none'
                      }}
                    />
                  ))}
                </div>
              </div>

              <button onClick={handleClearMap} style={styles.clearBtn}>
                {t("clearMarkersButton")}
              </button>

              {resultsMap && (
                <div style={styles.results}>
                  <h3 style={styles.resultsTitle}>{t("resultTitle")}</h3>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("point1")}</span>
                    <span style={styles.resultValue}>{resultsMap.point1}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("point2")}</span>
                    <span style={styles.resultValue}>{resultsMap.point2}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("distanceLabel")}</span>
                    <span style={styles.resultValue}>
                      {resultsMap.km.toFixed(2)} km ({resultsMap.miles.toFixed(2)} {t("milesUnit")})
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Introduction */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introPara1")}</p>
        <p style={styles.paragraph}>{t("introPara2")}</p>
        <p style={styles.paragraph}>{t("introPara3")}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("howToStep1")}</li>
          <li style={styles.listItem}>{t("howToStep2")}</li>
          <li style={styles.listItem}>{t("howToStep3")}</li>
          <li style={styles.listItem}>{t("howToStep4")}</li>
          <li style={styles.listItem}>{t("howToStep5")}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t("insightsSubtitle1")}
        </h3>
        <p style={styles.paragraph}>{t("insightsPara1")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t("insightsSubtitle2")}
        </h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("insightsUse1")}</li>
          <li style={styles.listItem}>{t("insightsUse2")}</li>
          <li style={styles.listItem}>{t("insightsUse3")}</li>
          <li style={styles.listItem}>{t("insightsUse4")}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t("insightsSubtitle3")}
        </h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("insightsTech1")}</li>
          <li style={styles.listItem}>{t("insightsTech2")}</li>
          <li style={styles.listItem}>{t("insightsTech3")}</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("bestPracticesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("bestPractice1")}</li>
          <li style={styles.listItem}>{t("bestPractice2")}</li>
          <li style={styles.listItem}>{t("bestPractice3")}</li>
          <li style={styles.listItem}>{t("bestPractice4")}</li>
          <li style={styles.listItem}>{t("bestPractice5")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>
            {t("faqQ1")}
          </h3>
          <p style={styles.paragraph}>{t("faqA1")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>
            {t("faqQ2")}
          </h3>
          <p style={styles.paragraph}>{t("faqA2")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>
            {t("faqQ3")}
          </h3>
          <p style={styles.paragraph}>{t("faqA3")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>
            {t("faqQ4")}
          </h3>
          <p style={styles.paragraph}>{t("faqA4")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>
            {t("faqQ5")}
          </h3>
          <p style={styles.paragraph}>{t("faqA5")}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <a href="https://www.anycoord.com/virtual-location/best-distance-calculator/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t("ref1")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://mapscaping.com/find-everything-within-any-radius/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t("ref2")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.mapize.com/distance-calculator-maps-their-importance-in-navigation" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t("ref3")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.maptive.com/feature/calculate-distances/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t("ref4")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://distancematrix.ai" target="_blank" rel="noopener noreferrer" style={{color: colors.primary}}>
              {t("ref5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}